/**
 * ScreenTile: 1 live monitor tile in the dashboard grid.
 *
 * Layout (top → bottom):
 *   1. Image area — widescreen screenshot, smooth double-buffer crossfade
 *   2. Info card  — employee name + status + current app/URL + last update
 *
 * Hover: shows "Xem to" overlay. Click anywhere opens FullscreenView.
 */
import { Maximize2, UserX, WifiOff } from 'lucide-react'
import { memo, useEffect, useState } from 'react'
import { formatRelativeTime } from '@/lib/format'
import { resolveScreenSrc } from '@/lib/axios'
import { cn } from '@/lib/cn'
import type { Employee } from '@monitor/shared'
import { Badge } from '@/components/ui/badge'
import { useScreen } from '@/hooks/use-screen-store'
import { joinEmployeeRoom } from '@/lib/socket'

const FADE_MS = 220

interface ScreenTileProps {
  employee: Employee
  onClick: () => void
}

function ScreenTileImpl({ employee, onClick }: ScreenTileProps) {
  const live = useScreen(employee.id)

  // Subscribe this tile's socket to the per-employee room. Without this,
  // server-side `io.to(\`employee:${id}\`).emit(SCREEN_NEW, ...)` only
  // reaches sockets that joined the room (e.g. FullscreenView). Tile
  // refresh is still driven by the broader `dashboard` room broadcast,
  // but explicitly joining guarantees the first-frame replay on mount
  // (sendLatestScreenshotFor) lands in this socket even after a quick
  // reconnect.
  useEffect(() => {
    joinEmployeeRoom(employee.id)
  }, [employee.id])

  // Zero-cost realtime: transient events carry `dataBase64` (no
  // meta.url, no Fly disk). resolveScreenSrc prefers it → data: URL,
  // so tiles render without any HTTP fetch. Persisted events fall
  // back to the file URL with a correct `&v=` cache-bust (capturedAt,
  // not meta.id which is 0 in transient mode).
  const newestUrl = live.latestScreen ? resolveScreenSrc(live.latestScreen) : null

  // Double-buffer: bottom = currently visible, top = newly arrived (fading in)
  const [bottomUrl, setBottomUrl] = useState<string | null>(null)
  const [topUrl, setTopUrl] = useState<string | null>(null)
  const [topLoaded, setTopLoaded] = useState(false)

  useEffect(() => {
    if (!newestUrl || newestUrl === topUrl) return
    setTopUrl(newestUrl)
    setTopLoaded(false)
  }, [newestUrl, topUrl])

  useEffect(() => {
    if (!topUrl || !topLoaded) return
    const t = setTimeout(() => {
      setBottomUrl(topUrl)
      setTopUrl(null)
      setTopLoaded(false)
    }, FADE_MS)
    return () => clearTimeout(t)
  }, [topUrl, topLoaded])

  const status = live.isOnline ? 'online' : employee.isActive ? 'offline' : 'inactive'
  const hostname = employee.agent?.hostname
  const osv = employee.agent?.osVersion
  const ip = employee.agent?.ipAddress

  // Compact OS label: "Windows 11 Pro 22631.4391" → "Win 11"
  const osShort = osv?.split(' ').slice(0, 2).join(' ')
  const osBadge = osv?.startsWith('Windows 10') ? 'Win 10'
    : osv?.startsWith('Windows 11') ? 'Win 11'
    : osv?.startsWith('Windows') ? osShort ?? 'Win'
    : null

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Screen of ${employee.fullName}`}
      className={cn(
        'group relative flex h-full w-full flex-col overflow-hidden bg-card text-left',
        'hover:ring-2 hover:ring-primary focus:outline-none focus:ring-2 focus:ring-primary',
      )}
    >
      {/* Image area — lock to 16:9 so wide tiles don't stretch the screenshot.
          Previously used flex-[5] which gave a 1.21:1 frame on 3-up grid (tile
          width 347px, image height ~287px), and object-cover then cropped the
          screenshot to a narrow vertical slice -- admin only saw the middle
          column. aspect-video (16:9) keeps the screenshot readable. */}
      <div className="relative aspect-video w-full overflow-hidden bg-slate-900">
        {bottomUrl && (
          <img
            src={bottomUrl}
            alt=""
            className={cn(
              'absolute inset-0 h-full w-full object-cover transition-[filter,opacity]',
              status === 'offline' && 'opacity-40 blur-[1px] saturate-50',
            )}
            draggable={false}
          />
        )}
        {topUrl && (
          <img
            src={topUrl}
            alt=""
            onLoad={() => setTopLoaded(true)}
            className={cn(
              'absolute inset-0 h-full w-full object-cover transition-opacity',
              topLoaded ? 'opacity-100' : 'opacity-0',
              status === 'offline' && 'opacity-40 blur-[1px] saturate-50',
            )}
            style={{ transitionDuration: `${FADE_MS}ms` }}
            draggable={false}
          />
        )}
        {/* Offline overlay: when status is offline, ALWAYS show a centered
            badge so admin sees at-a-glance that the cached screenshot is
            stale. We deliberately show it on top of any cached image (not
            only when bottomUrl/topUrl are null) because otherwise NV06 after
            shutdown looks identical to NV06 while live. This single overlay
            replaces the older "Chưa kết nối / Đã nghỉ việc / Chưa có ảnh"
            placeholder -- otherwise they stack and overlap. */}
        {status !== 'online' && (
          <div className="absolute inset-0 z-[5] flex flex-col items-center justify-center gap-1.5 text-slate-300">
            {status === 'inactive' ? (
              <UserX className="h-7 w-7" aria-hidden />
            ) : (
              <WifiOff className="h-7 w-7" aria-hidden />
            )}
            <span className="text-xs font-medium">
              {status === 'inactive' ? 'Đã nghỉ việc' : 'Mất kết nối'}
            </span>
            {live.lastHeartbeat && status === 'offline' && (
              <span className="text-[10px] text-slate-400">
                {formatRelativeTime(live.lastHeartbeat)}
              </span>
            )}
          </div>
        )}

        {/* Status dot — góc trên-phải của ảnh, luôn hiển thị */}
        <span
          className={cn(
            'absolute right-2 top-2 z-10 h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-black/40 shadow',
            status === 'online' && 'animate-pulse-dot bg-emerald-400',
            status === 'offline' && 'bg-slate-400',
            status === 'inactive' && 'bg-red-400',
          )}
          title={status}
        />

        {/* Hover overlay with "Xem to" -- only when online. When offline /
            inactive we already show the offline overlay (Mất kết nối /
            Đã nghỉ việc) and we don't want the white "Xem to" pill to
            float on top of it and look like a z-index bug. */}
        {status === 'online' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-opacity group-hover:bg-black/40 group-hover:opacity-100">
            <span className="flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-xs font-medium text-slate-900 shadow">
              <Maximize2 className="h-3.5 w-3.5" />
              Xem to
            </span>
          </div>
        )}
      </div>

      {/* Info card — 1 dòng duy nhất, compact */}
      <div className="flex flex-row items-center gap-2 border-t bg-background px-2.5 py-1.5 text-[11px] leading-none">
        <span
          className="truncate font-medium"
          title={employee.fullName}
        >
          {employee.fullName}
        </span>
        <span
          className="shrink-0 truncate text-muted-foreground"
          title={employee.employeeCode}
        >
          · {employee.employeeCode}
        </span>
        {hostname && (
          <span
            className="ml-auto shrink-0 truncate font-mono text-[10px] text-muted-foreground/80"
            title={`Máy: ${hostname}${ip ? ` · IP: ${ip}` : ''}${osv ? ` · OS: ${osv}` : ''}`}
          >
            {hostname}
          </span>
        )}
        {osBadge && (
          <Badge variant="outline" className="shrink-0 px-1 py-0 text-[9px] font-normal">
            {osBadge}
          </Badge>
        )}
        <span
          className="shrink-0 text-[10px] text-muted-foreground/70"
          title={live.lastHeartbeat ? new Date(live.lastHeartbeat).toLocaleString() : undefined}
        >
          {formatRelativeTime(live.lastHeartbeat)}
        </span>
      </div>
    </button>
  )
}

export const ScreenTile = memo(ScreenTileImpl)
